import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RedirectToSignIn, SignedIn, SignedOut } from "@clerk/clerk-react";
import { SearchPage } from "./pages/SearchPage";
import { RagSearchPage } from "./pages/RagSearchPage";
import { AdminLayout } from "./components/admin/layout";
import { RecordsPage } from "./pages/admin/RecordsPage";
import { DashboardPage } from "./pages/admin/DashboardPage";
import { DebugPage } from "./pages/admin/DebugPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChatProvider } from "./contexts/ChatContext";
import { UsageProvider } from "./contexts/UsageContext";
import { EditRecordPage } from "./pages/admin/EditRecordPage";
import { CreateRecordPage } from "./pages/admin/CreateRecordPage";
import { DocumentVersionsPage } from "./pages/admin/DocumentVersionsPage";
import { ConversationsPage } from "./pages/admin/ConversationsPage";
import { ConversationDetailPage } from "./pages/admin/ConversationDetailPage";
import { AgentsPage } from "./pages/admin/AgentsPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { RolesPermissionsPage } from "./pages/admin/RolesPermissionsPage";
import { WritingAssistantSettingsPage } from "./pages/admin/WritingAssistantSettingsPage";
import { PlansPage } from "./pages/admin/PlansPage";
import { SubscriptionsPage } from "./pages/admin/SubscriptionsPage";
import { HealthCheck } from "./components/HealthCheck";
import { AuthSetup } from "./components/AuthSetup";
import { ChatPage } from "./pages/ChatPage";
import { AdminGuard } from "./guards/RoleGuard";
import { BlockedUserScreen } from "./components/BlockedUserScreen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  },
});

/**
 * AuthenticatedApp - Renders the main app or blocked screen based on user status
 */
function AuthenticatedApp() {
  const { isBlocked } = useAuth();

  // Show blocked screen if user is blocked
  if (isBlocked) {
    return <BlockedUserScreen />;
  }

  return (
    <UsageProvider>
      <>
        <AuthSetup />
        <HealthCheck />
        <Routes>
        <Route path="/" element={<Navigate to="/chat" replace />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/rag" element={<RagSearchPage />} />
        {/* Admin routes protected by AdminGuard - Feature: 001-user-role-management (T040) */}
        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="records" element={<RecordsPage />} />
          <Route path="records/create" element={<CreateRecordPage />} />
          <Route path="records/:id/edit" element={<EditRecordPage />} />
          <Route path="records/:id/versions" element={<DocumentVersionsPage />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route
            path="conversations/:conversationId"
            element={<ConversationDetailPage />}
          />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPermissionsPage />} />
          <Route path="writing-assistant" element={<WritingAssistantSettingsPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="debug" element={<DebugPage />} />
          <Route
            index
            element={<Navigate to="/admin/dashboard" replace />}
          />
        </Route>
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:conversationId" element={<ChatPage />} />
        </Routes>
      </>
    </UsageProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ChatProvider>
          <Router>
            <SignedIn>
              <AuthenticatedApp />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn redirectUrl="/chat" />
            </SignedOut>
          </Router>
        </ChatProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
